import React from "react";
import { Link } from "react-router-dom";
import { type VariantProps } from "class-variance-authority";

import { buttonVariants } from "./variants";
import { cn } from "@/libs/cn";

export type buttonColorVariants = "primary" | "success" | "warning" | "danger";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  color?: buttonColorVariants;
  disabled?: boolean;
  label?: string;
  iconPosition?: "left" | "right";
  icon?: React.ReactNode;
  route?: string;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, label, color, size, disabled, icon, route, iconPosition, loading, ...props }, ref) => {
    const customClass = cn("inline-flex items-center justify-center", buttonVariants({ variant, color, size, disabled, className }));

    const content = (
      <div className={`flex items-center justify-center gap-2 ${iconPosition === "right" ? "flex-row-reverse" : "flex-row"}`}>
        {icon}
        {label && <p>{label}</p>}
      </div>
    );

    if (route)
      return (
        <Link to={route} className={customClass}>
          {content}
        </Link>
      );

    return (
      <button className={customClass} disabled={disabled || loading} ref={ref} {...props}>
        {loading ? <ButtonSpinner /> : content}
      </button>
    );
  },
);
Button.displayName = "Button";

export default Button;

export const ButtonSpinner = () => (
  <div className="relative w-4 h-4 mx-auto">
    {[...Array(12)].map((_, i) => (
      <div key={i} className="absolute inset-0" style={{ transform: `rotate(${i * 30}deg)` }}>
        <div className="w-1 h-1 bg-current rounded-full mx-auto animate-spinner-dot" style={{ animationDelay: `${i * 0.05}s` }} />
      </div>
    ))}
  </div>
);
